import React, { PointerEventHandler, ReactNode } from 'react';
import { alpha } from '@mui/material/styles';
import { useTheme } from '../../hooks/useTheme';
import { Stack } from '../stack';
import { MOBILE_BREAKPOINT_THRESHOLD, SxProps } from '../../theme';
import { mergeSx } from '../../utils/sx/merge';
import { Typography } from '../typography';
import { Box } from '../box';

export interface AlertProps {
  icon?: ReactNode;
  title?: ReactNode;
  children?: ReactNode | ReactNode[];
  onClick?: PointerEventHandler<HTMLDivElement>;
  sx?: SxProps;
}

export const Alert: React.FC<AlertProps> = ({ icon, children, title, onClick, sx }) => {
  const theme = useTheme();

  return (
    <Stack
      direction="row"
      gap={1}
      onClick={onClick}
      sx={mergeSx(
        {
          py: 1,
          px: 1.5,
          borderRadius: '12px',
          cursor: onClick ? 'pointer' : 'default',
          boxShadow: `0px 2.16656px 3.24983px 0px ${alpha(theme.palette.black, 0.1)}`,
          backgroundColor: theme.palette.background.default,
          alignItems: { xs: 'flex-start', [MOBILE_BREAKPOINT_THRESHOLD]: 'center' },
        },
        sx,
      )}
      {...(onClick ? { tabIndex: 0, role: 'button' } : undefined)}
    >
      <Typography
        component="div"
        sx={{ fontFamily: '"Source Sans Pro", "Helvetica Neue", Helvetica, Arial, sans-serif' }}
        variant="button secondary"
      >
        {icon}
      </Typography>
      <Box component="div">
        {title ? (
          <Typography component="div" variant="caption bold">
            {title}
          </Typography>
        ) : null}
        <Typography component="div" variant="caption">
          {children}
        </Typography>
      </Box>
    </Stack>
  );
};
